import { useEffect, useMemo, useState } from "react";
import type { MatchProgress } from "../lib/api";
import { describeProgress, fmtDuration, prettyEventName, windowLabel } from "../lib/format";
import { useApp } from "../lib/store";
import { CopyButton } from "./bits";

const PROGRESS_POLL_MS = 5000;

export function RosterDrawer() {
  const {
    selectedSession,
    matchDetail: detail,
    matchLoading: loading,
    refreshMatch,
    closeMatch,
    markCopied,
  } = useApp();
  const [filter, setFilter] = useState("");

  useEffect(() => setFilter(""), [selectedSession]);

  // keep an in-flight match fresh so the progress block moves
  const parsing = detail?.status === "watching" || detail?.status === "discovered";
  useEffect(() => {
    if (!parsing) return;
    const t = setInterval(refreshMatch, PROGRESS_POLL_MS);
    return () => clearInterval(t);
  }, [parsing, refreshMatch]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMatch();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeMatch]);

  const players = useMemo(() => {
    const all = detail?.players ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      p => p.display_name.toLowerCase().includes(q) || p.epic_id.toLowerCase().includes(q),
    );
  }, [detail, filter]);

  if (!selectedSession) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]" onClick={closeMatch} />
      <aside className="drawer-in fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-border bg-surface shadow-[-24px_0_60px_rgba(0,0,0,0.45)]">
        <header className="border-b border-border px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="label-xs">Lobby roster</p>
              {detail && (
                <p className="mt-1 truncate text-[13px] text-dim">
                  {prettyEventName(detail.event_id)}{" "}
                  <span className="text-faint">
                    · {windowLabel(detail.event_window_id, detail.event_id)}
                  </span>
                </p>
              )}
              <code className="mt-1 block break-all font-mono text-[11px] text-faint">
                {selectedSession}
              </code>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <CopyButton text={selectedSession} onCopy={() => markCopied(selectedSession)} />
              <button
                onClick={closeMatch}
                className="rounded-md border border-border px-2.5 py-1 text-xs text-dim transition hover:border-border-strong hover:text-fg"
              >
                Esc
              </button>
            </div>
          </div>

          {detail?.players && (
            <div className="mt-3.5 flex gap-5">
              <HeaderStat value={detail.players.length} label="players" />
              <HeaderStat value={detail.chunks_parsed ?? "—"} label="chunks" />
              <HeaderStat value={detail.players.filter(p => p.is_bot).length} label="bots" />
            </div>
          )}
        </header>

        {detail?.status === "failed" && (
          <p className="mx-5 mt-4 rounded-lg border border-err/30 bg-err/[0.07] px-3.5 py-2.5 text-xs text-err">
            {detail.detail ?? "parse failed"}
          </p>
        )}

        {detail?.status === "watching" && detail.progress && (
          <ParseProgress progress={detail.progress} />
        )}

        {detail?.players && detail.players.length > 0 && (
          <div className="border-b border-border px-5 py-2.5">
            <input
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Filter roster…"
              spellCheck={false}
              autoFocus
              className="w-full bg-transparent text-[13px] text-fg outline-none placeholder:text-faint"
            />
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading && !detail ? (
            <p className="px-5 py-5 text-xs text-faint">Loading roster…</p>
          ) : detail && (!detail.players || detail.players.length === 0) && detail.status !== "failed" ? (
            <p className="px-5 py-8 text-center text-xs text-faint">
              {detail.status === "watching" || detail.status === "discovered"
                ? "Still parsing — the roster lands when the lobby stabilises."
                : "Replay not parsed for this match — only the official result is on record."}
            </p>
          ) : (
            <ul>
              {players.map((p, i) => (
                <li
                  key={p.epic_id}
                  className="flex items-center gap-3 border-b border-border/50 px-5 py-2 transition hover:bg-raised"
                >
                  <span className="w-7 shrink-0 text-right font-mono text-[10.5px] text-faint">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[13px] text-fg">
                    {p.display_name || <span className="italic text-faint">unknown</span>}
                    {p.is_bot && (
                      <span className="ml-2 rounded border border-border-strong px-1 font-mono text-[9px] uppercase tracking-wider text-dim">
                        bot
                      </span>
                    )}
                  </span>
                  <code className="shrink-0 font-mono text-[10.5px] text-faint">
                    {p.epic_id.slice(0, 8)}
                  </code>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
}

function HeaderStat({ value, label }: { value: number | string; label: string }) {
  return (
    <span>
      <span className="font-mono text-[13px] font-semibold text-fg">{value}</span>{" "}
      <span className="text-[11px] text-faint">{label}</span>
    </span>
  );
}

/** Live view of what the watcher is doing right now. */
function ParseProgress({ progress: p }: { progress: MatchProgress }) {
  return (
    <div className="border-b border-border bg-raised/40 px-5 py-3.5">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 font-mono text-[11.5px] text-accent">
          <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-accent" />
          {describeProgress(p)}
        </span>
        <span className="font-mono text-[11px] text-faint">
          {fmtDuration(p.elapsed_s)}
          {p.idle_s > 90 && <span className="text-warn"> · idle {fmtDuration(p.idle_s)}</span>}
        </span>
      </div>

      {p.data_total > 0 && <MeterRow label="data" done={p.data_chunks} total={p.data_total} />}
      {p.event_total > 0 && (
        <MeterRow label="events" done={p.event_chunks} total={p.event_total} />
      )}

      <div className="mt-2.5 flex gap-4 font-mono text-[11px] text-faint">
        {p.roster > 0 && <span>{p.roster} players</span>}
        {p.elims > 0 && <span>{p.elims} elims</span>}
        {p.live && <span className="text-ok">match still live</span>}
      </div>
    </div>
  );
}

function MeterRow({ label, done, total }: { label: string; done: number; total: number }) {
  return (
    <div className="mt-2.5 flex items-center gap-2.5">
      <span className="label-xs w-10 shrink-0">{label}</span>
      <span className="h-1 flex-1 overflow-hidden rounded-full bg-border">
        <span
          className="block h-full rounded-full bg-accent/80 transition-[width] duration-700"
          style={{ width: `${Math.round(Math.min(done / total, 1) * 100)}%` }}
        />
      </span>
      <span className="w-16 shrink-0 text-right font-mono text-[11px] text-dim">
        {done}/{total}
      </span>
    </div>
  );
}
